import { useState, useEffect } from 'react'
import { useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, TrendingDown, TrendingUp, Clock, Users, Layers, DollarSign } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ResponsiveContainer,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts'

type ChartMogulMonthly = {
  date: string
  mrr: number
  arr: number
  customers: number
  customerChurnRate: number
  mrrChurnRate: number
  ltv: number
  arpa: number
  asp: number
}

type UnitEconomicsData = {
  monthly: ChartMogulMonthly[]
  current: {
    mrr: number
    arr: number
    customers: number
    customerChurnRate: number
    mrrChurnRate: number
    ltv: number
    arpa: number
    estimatedLtv: number
  }
}

type EngagementMonth = {
  month: string
  projectsCreated: number
  activeUsers: number
  videosGenerated: number
  totalSessionMinutes: number
  avgSessionMinutes: number
  sessionCount: number
}

type CostMonth = {
  month: string
  totalCostCents: number
  revenueCents: number
  googleAdsCents: number
  variableCostCents: number
  infraCostCents: number
}

function centsToUsd(cents: number): string {
  const dollars = cents / 100
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}k`
  return `$${dollars.toFixed(0)}`
}

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`
}

function formatMonth(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

function formatUsd(cents: number): string {
  const abs = Math.abs(cents / 100)
  const sign = cents < 0 ? '-' : ''
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`
  return `${sign}$${abs.toFixed(0)}`
}

function ChartTooltipContent({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-md border border-neutral-200 bg-white px-3 py-2 shadow-sm text-xs">
      <div className="font-medium text-neutral-900 mb-1">{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: p.color }}
          />
          <span className="text-neutral-600">{p.name}</span>
          <span className="ml-auto font-medium text-neutral-900">
            {typeof p.value === 'number'
              ? p.dataKey?.includes('Rate') || p.dataKey?.includes('churn')
                ? `${p.value.toFixed(1)}%`
                : p.dataKey?.includes('Minutes') || p.dataKey?.includes('minutes')
                  ? `${p.value.toFixed(0)}m`
                  : p.dataKey?.includes('mrr') || p.dataKey?.includes('ltv') || p.dataKey?.includes('arpa') || p.dataKey === 'cohortValue' || p.dataKey === 'costs'
                    ? formatUsd(p.value)
                    : p.value.toLocaleString()
              : p.value}
          </span>
        </div>
      ))}
    </div>
  )
}

export function UnitEconomicsDashboard() {
  const fetchMetrics = useAction(api.chartmogul.getUnitEconomicsMetrics)
  const fetchEngagement = useAction(api.adminQueries.getEngagementTimeSeries)
  const fetchCostHistory = useAction(api.adminQueries.getMonthlyCostHistory)
  const [data, setData] = useState<UnitEconomicsData | null>(null)
  const [engagement, setEngagement] = useState<EngagementMonth[] | null>(null)
  const [costHistory, setCostHistory] = useState<CostMonth[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    Promise.allSettled([
      fetchMetrics().then(setData),
      fetchEngagement().then(setEngagement),
      fetchCostHistory().then(setCostHistory),
    ])
      .then((results) => {
        const failures = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[]
        if (failures.length === results.length) {
          setError(failures[0].reason?.message ?? 'Failed to fetch metrics')
        }
        for (const f of failures) {
          console.error('[UnitEconomics] Fetch failed:', f.reason)
        }
      })
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-red-500">
        Failed to load metrics: {error}
      </div>
    )
  }

  if (!data) return null

  const { current, monthly } = data

  const mrrChartData = monthly.map((m) => ({
    label: formatMonth(m.date),
    mrr: m.mrr,
    customers: m.customers,
  }))

  const churnChartData = monthly.map((m) => ({
    label: formatMonth(m.date),
    customerChurnRate: m.customerChurnRate,
    mrrChurnRate: m.mrrChurnRate,
  }))

  const ltvChartData = monthly.map((m) => ({
    label: formatMonth(m.date),
    ltv: m.ltv,
    arpa: m.arpa,
  }))

  const engagementChartData = (engagement ?? []).map((e) => ({
    label: formatMonth(e.month + '-01'),
    projects: e.projectsCreated,
    activeUsers: e.activeUsers,
    videos: e.videosGenerated,
  }))

  const sessionChartData = (engagement ?? []).map((e) => ({
    label: formatMonth(e.month + '-01'),
    totalMinutes: e.totalSessionMinutes,
    avgMinutes: e.avgSessionMinutes,
    sessions: e.sessionCount,
  }))

  const costByMonth = new Map(
    (costHistory ?? []).map((c) => [c.month, c])
  )
  const latestCost = costHistory?.[costHistory.length - 1] ?? null

  let projectedMonthlyCost = 0
  let projectedVariableCost = 0
  let projectedGoogleAds = 0
  let isPartialMonth = false

  if (latestCost) {
    const now = new Date()
    const [y, m] = latestCost.month.split('-').map(Number)
    const daysInMonth = new Date(y, m, 0).getDate()
    isPartialMonth =
      now.getFullYear() === y && now.getMonth() + 1 === m && now.getDate() < daysInMonth
    const scale = isPartialMonth ? daysInMonth / now.getDate() : 1
    projectedMonthlyCost = Math.round(latestCost.totalCostCents * scale)
    projectedVariableCost = Math.round(latestCost.variableCostCents * scale)
    projectedGoogleAds = Math.round(latestCost.googleAdsCents * scale)
  }

  const monthlyBurn = projectedMonthlyCost - current.mrr

  const varCostPerCustomer =
    current.customers > 0 ? projectedVariableCost / current.customers : 0
  const lifetimeMonths =
    current.customerChurnRate > 0 ? 100 / current.customerChurnRate : 0

  const prevMonth = monthly.length >= 2 ? monthly[monthly.length - 2] : null
  const netNew = prevMonth ? current.customers - prevMonth.customers : current.customers
  const churned = prevMonth
    ? Math.round(prevMonth.customers * current.customerChurnRate / 100)
    : 0
  const estimatedNewCustomers = Math.max(1, netNew + churned)
  const cac = projectedGoogleAds / estimatedNewCustomers
  const cohortValue = estimatedNewCustomers * current.estimatedLtv

  const lifetimeVarCost = varCostPerCustomer * lifetimeMonths
  const unitProfit = current.estimatedLtv - cac - lifetimeVarCost
  const ltvCacRatio = cac > 0 ? current.estimatedLtv / cac : 0
  const contributionMargin = current.arpa - varCostPerCustomer
  const paybackMonths = contributionMargin > 0 ? cac / contributionMargin : 0

  const plChartData = monthly
    .map((m, i) => {
      const monthKey = m.date.slice(0, 7)
      const costs = costByMonth.get(monthKey)
      const prev = i > 0 ? monthly[i - 1] : null
      const mChurned = prev ? Math.round(prev.customers * m.customerChurnRate / 100) : 0
      const mNew = prev ? Math.max(0, m.customers - prev.customers + mChurned) : m.customers
      return {
        label: formatMonth(m.date),
        cohortValue: mNew * m.ltv,
        costs: costs?.totalCostCents ?? 0,
      }
    })
    .filter((d) => d.costs > 0)

  const churnTrend =
    monthly.length >= 2
      ? monthly[monthly.length - 1].customerChurnRate -
        monthly[monthly.length - 2].customerChurnRate
      : 0

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
              Est. LTV
            </p>
            <p className="text-2xl font-bold text-neutral-900 mt-1">
              {centsToUsd(current.estimatedLtv)}
            </p>
            <p className="text-xs text-neutral-400 mt-0.5">
              ARPU / churn rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">MRR</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">
              {centsToUsd(current.mrr)}
            </p>
            <p className="text-xs text-neutral-400 mt-0.5">
              {current.customers} paying customers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
              Monthly Churn
            </p>
            <div className="flex items-center gap-2 mt-1">
              <p className={cn(
                'text-2xl font-bold',
                current.customerChurnRate > 5 ? 'text-red-600' : 'text-neutral-900'
              )}>
                {formatPct(current.customerChurnRate)}
              </p>
              {churnTrend !== 0 && (
                <span className={cn(
                  'inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded',
                  churnTrend > 0
                    ? 'bg-red-50 text-red-600'
                    : 'bg-emerald-50 text-emerald-600'
                )}>
                  {churnTrend > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {Math.abs(churnTrend).toFixed(1)}pp
                </span>
              )}
            </div>
            <p className="text-xs text-neutral-400 mt-0.5">customer churn</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">ARPU</p>
            <p className="text-2xl font-bold text-neutral-900 mt-1">
              {centsToUsd(current.arpa)}
            </p>
            <p className="text-xs text-neutral-400 mt-0.5">per month</p>
          </CardContent>
        </Card>
      </div>

      {/* P&L Estimate */}
      {latestCost && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-5 pb-4">
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
                  Monthly Cohort Value
                </p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {formatUsd(cohortValue)}
                </p>
                <p className="text-xs text-neutral-400 mt-0.5">
                  {estimatedNewCustomers} new × {formatUsd(current.estimatedLtv)} LTV
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5 pb-4">
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
                  Monthly Costs
                </p>
                <p className="text-2xl font-bold text-orange-600 mt-1">
                  {formatUsd(projectedMonthlyCost)}
                </p>
                <p className="text-xs text-neutral-400 mt-0.5">
                  {isPartialMonth ? 'projected' : 'actual'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5 pb-4">
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
                  Monthly Burn
                </p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {formatUsd(monthlyBurn)}
                </p>
                <p className="text-xs text-neutral-400 mt-0.5">
                  costs − MRR
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5 pb-4">
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
                  Break-even MRR
                </p>
                <p className="text-2xl font-bold text-neutral-900 mt-1">
                  {formatUsd(projectedMonthlyCost)}
                </p>
                <p className="text-xs text-neutral-400 mt-0.5">
                  target to cover costs
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-emerald-500" />
                Per-Customer Unit Economics
              </CardTitle>
              <CardDescription>
                Forward-looking projection from current metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-4 text-center">
                <div>
                  <p className="text-xs text-neutral-500">LTV</p>
                  <p className="text-lg font-semibold text-blue-600">
                    {formatUsd(current.estimatedLtv)}
                  </p>
                  <p className="text-xs text-neutral-400">
                    ~{lifetimeMonths.toFixed(0)} mo
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">CAC</p>
                  <p className="text-lg font-semibold text-orange-600">
                    {formatUsd(cac)}
                  </p>
                  <p className="text-xs text-neutral-400">ads / new cust</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">LTV:CAC</p>
                  <p
                    className={cn(
                      'text-lg font-semibold',
                      ltvCacRatio >= 3
                        ? 'text-emerald-600'
                        : ltvCacRatio >= 1
                          ? 'text-amber-600'
                          : 'text-red-600'
                    )}
                  >
                    {ltvCacRatio > 0 ? `${ltvCacRatio.toFixed(1)}x` : '—'}
                  </p>
                  <p className="text-xs text-neutral-400">
                    {ltvCacRatio >= 3
                      ? 'healthy'
                      : ltvCacRatio >= 1
                        ? 'thin'
                        : 'negative'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">Var. Cost/mo</p>
                  <p className="text-lg font-semibold text-neutral-900">
                    {formatUsd(varCostPerCustomer)}
                  </p>
                  <p className="text-xs text-neutral-400">per customer</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">Contribution</p>
                  <p
                    className={cn(
                      'text-lg font-semibold',
                      contributionMargin > 0 ? 'text-emerald-600' : 'text-red-600'
                    )}
                  >
                    {formatUsd(contributionMargin)}
                  </p>
                  <p className="text-xs text-neutral-400">ARPU - var cost</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">Payback</p>
                  <p className="text-lg font-semibold text-neutral-900">
                    {paybackMonths > 0 ? `${paybackMonths.toFixed(1)} mo` : '—'}
                  </p>
                  <p className="text-xs text-neutral-400">CAC recovery</p>
                </div>
              </div>

              {current.estimatedLtv > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2 text-xs text-neutral-500 mb-2">
                    <span>LTV Breakdown</span>
                    <span className="text-neutral-300">|</span>
                    <span>
                      {formatUsd(current.estimatedLtv)} over ~{lifetimeMonths.toFixed(0)} months
                    </span>
                  </div>
                  <div className="h-5 flex rounded overflow-hidden text-[10px] font-medium">
                    <div
                      className="bg-orange-400 text-white flex items-center justify-center"
                      style={{
                        width: `${Math.max(0, Math.min(50, (cac / current.estimatedLtv) * 100)).toFixed(1)}%`,
                        minWidth: cac > 0 ? 28 : 0,
                      }}
                    >
                      CAC
                    </div>
                    <div
                      className="bg-amber-200 text-amber-800 flex items-center justify-center"
                      style={{
                        width: `${Math.max(0, Math.min(70, (lifetimeVarCost / current.estimatedLtv) * 100)).toFixed(1)}%`,
                        minWidth: lifetimeVarCost > 0 ? 32 : 0,
                      }}
                    >
                      Costs
                    </div>
                    <div
                      className={cn(
                        'flex-1 flex items-center justify-center',
                        unitProfit >= 0
                          ? 'bg-emerald-400 text-white'
                          : 'bg-red-400 text-white'
                      )}
                    >
                      {unitProfit >= 0 ? '+' : ''}{formatUsd(unitProfit)}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-500" />
                Cohort Value vs Costs
              </CardTitle>
              <CardDescription>LTV of new customers acquired each month vs that month's costs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {plChartData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-neutral-400">
                    No cost data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={plChartData}
                      margin={{ top: 8, right: 12, left: 4, bottom: 0 }}
                    >
                      <CartesianGrid stroke="#f1f5f9" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                        axisLine={{ stroke: '#e2e8f0' }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => formatUsd(v)}
                        width={56}
                      />
                      <Tooltip content={<ChartTooltipContent />} />
                      <Legend
                        iconType="circle"
                        wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                      />
                      <Bar
                        dataKey="cohortValue"
                        name="Cohort LTV"
                        fill="#3b82f6"
                        opacity={0.8}
                        radius={[3, 3, 0, 0]}
                      />
                      <Bar
                        dataKey="costs"
                        name="Costs"
                        fill="#f97316"
                        opacity={0.8}
                        radius={[3, 3, 0, 0]}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* MRR + Churn Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              MRR & Customers
            </CardTitle>
            <CardDescription>Monthly recurring revenue trend</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              {mrrChartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-neutral-400">
                  No data
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={mrrChartData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                    <YAxis yAxisId="mrr" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => centsToUsd(v)} width={52} />
                    <YAxis yAxisId="customers" orientation="right" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} width={32} />
                    <Tooltip content={<ChartTooltipContent />} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    <Area yAxisId="mrr" type="monotone" dataKey="mrr" name="MRR" stroke="#10b981" strokeWidth={2} fill="url(#mrrFill)" dot={false} activeDot={{ r: 4 }} />
                    <Line yAxisId="customers" type="monotone" dataKey="customers" name="Customers" stroke="#8b5cf6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} strokeDasharray="4 4" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-rose-500" />
              Churn Rate
            </CardTitle>
            <CardDescription>Customer & MRR churn per month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              {churnChartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-neutral-400">
                  No data
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={churnChartData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} width={40} />
                    <Tooltip content={<ChartTooltipContent />} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    <Line type="monotone" dataKey="customerChurnRate" name="Customer Churn" stroke="#f43f5e" strokeWidth={2} dot={{ fill: '#f43f5e', r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="mrrChurnRate" name="MRR Churn" stroke="#f97316" strokeWidth={2} dot={{ fill: '#f97316', r: 3 }} activeDot={{ r: 5 }} strokeDasharray="4 4" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* LTV + ARPA */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-500" />
            LTV & ARPU Over Time
          </CardTitle>
          <CardDescription>Customer lifetime value and average revenue per user</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[260px]">
            {ltvChartData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-neutral-400">
                No data
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={ltvChartData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ltvFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                  <YAxis yAxisId="ltv" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => centsToUsd(v)} width={52} />
                  <YAxis yAxisId="arpa" orientation="right" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => centsToUsd(v)} width={52} />
                  <Tooltip content={<ChartTooltipContent />} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Area yAxisId="ltv" type="monotone" dataKey="ltv" name="LTV" stroke="#3b82f6" strokeWidth={2} fill="url(#ltvFill)" dot={false} activeDot={{ r: 4 }} />
                  <Line yAxisId="arpa" type="monotone" dataKey="arpa" name="ARPU" stroke="#8b5cf6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} strokeDasharray="4 4" />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Engagement Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="h-4 w-4 text-amber-500" />
              Product Activity
            </CardTitle>
            <CardDescription>Monthly projects, users, and video generations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              {engagement === null ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
                </div>
              ) : engagementChartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-neutral-400">
                  No data
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={engagementChartData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} width={32} />
                    <Tooltip content={<ChartTooltipContent />} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    <Bar dataKey="projects" name="Projects" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="activeUsers" name="Active Users" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="videos" name="Videos" fill="#06b6d4" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-teal-500" />
              Session Time
            </CardTitle>
            <CardDescription>Time spent in projects per month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              {engagement === null ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
                </div>
              ) : sessionChartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-neutral-400">
                  No data
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={sessionChartData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="sessionFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#14b8a6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                    <YAxis yAxisId="total" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`} width={40} />
                    <YAxis yAxisId="avg" orientation="right" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}m`} width={40} />
                    <Tooltip content={<ChartTooltipContent />} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    <Area yAxisId="total" type="monotone" dataKey="totalMinutes" name="Total Minutes" stroke="#14b8a6" strokeWidth={2} fill="url(#sessionFill)" dot={false} activeDot={{ r: 4 }} />
                    <Line yAxisId="avg" type="monotone" dataKey="avgMinutes" name="Avg Session" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} activeDot={{ r: 5 }} strokeDasharray="4 4" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* LTV Context Card */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 text-center">
            <div>
              <p className="text-xs text-neutral-500">Customer LTV</p>
              <p className="text-lg font-semibold text-neutral-900">{centsToUsd(current.ltv)}</p>
              <p className="text-xs text-neutral-400">ChartMogul</p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">Est. LTV (ARPU/Churn)</p>
              <p className="text-lg font-semibold text-blue-600">{centsToUsd(current.estimatedLtv)}</p>
              <p className="text-xs text-neutral-400">
                {current.customerChurnRate > 0
                  ? `~${Math.round(100 / current.customerChurnRate)} mo lifetime`
                  : 'no churn data'}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">MRR Churn Rate</p>
              <p className={cn(
                'text-lg font-semibold',
                current.mrrChurnRate > 5 ? 'text-red-600' : 'text-neutral-900'
              )}>
                {formatPct(current.mrrChurnRate)}
              </p>
              <p className="text-xs text-neutral-400">revenue-weighted</p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">ARR</p>
              <p className="text-lg font-semibold text-emerald-600">{centsToUsd(current.arr)}</p>
              <p className="text-xs text-neutral-400">annualized</p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">LTV / ARPU Ratio</p>
              <p className="text-lg font-semibold text-neutral-900">
                {current.arpa > 0 ? `${(current.estimatedLtv / current.arpa).toFixed(1)}x` : '—'}
              </p>
              <p className="text-xs text-neutral-400">months of revenue</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
